import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, imageUrl } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { countryFlagUrl } from "@/lib/countries";
import { toast } from "sonner";
import { Check, X, Trash2, Star, Trophy, RotateCcw } from "lucide-react";

function FlagToggle({ active, onClick, label, color, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`inline-flex h-7 w-7 items-center justify-center border transition-colors ${
        active ? `${color} text-white border-transparent` : "bg-white text-muted-foreground border-border hover:border-foreground"
      }`}
      data-testid={testid}
    >
      {label === "Star" ? <Star className={`h-3.5 w-3.5 ${active ? "fill-current" : ""}`} /> : <Trophy className="h-3.5 w-3.5" />}
    </button>
  );
}

function PlayerCard({ p, onToggleFlag, children }) {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-border last:border-b-0" data-testid={`admin-player-row-${p.id}`}>
      <div className="h-14 w-14 bg-secondary border border-border overflow-hidden flex items-center justify-center flex-shrink-0">
        {p.photo_path ? (
          <img src={imageUrl(p.photo_path)} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="w-6 h-6 rounded-full othello-disc-dark" />
        )}
      </div>
      <img src={countryFlagUrl(p.country_code)} alt="" className="h-6 w-9 object-cover border border-border" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{p.name}</div>
        <div className="text-xs text-muted-foreground">
          {p.country_name || p.country_code}
          {p.is_male && " · Male"}
          {p.is_female && " · Female"}
          {p.is_youth && " · Youth"}
          {p.is_star && " · Star"}
          {p.is_trophy && " · Champion"}
        </div>
        {p.email && (
          <a
            href={`mailto:${p.email}`}
            className="text-xs text-primary hover:underline truncate block"
            data-testid={`player-email-${p.id}`}
          >
            {p.email}
          </a>
        )}
      </div>
      <div className="flex items-center gap-1 mr-2 border-r border-border pr-3">
        <FlagToggle
          active={!!p.is_star}
          onClick={() => onToggleFlag(p, "is_star", !p.is_star)}
          label="Star"
          color="bg-yellow-400"
          testid={`star-toggle-${p.id}`}
        />
        <FlagToggle
          active={!!p.is_trophy}
          onClick={() => onToggleFlag(p, "is_trophy", !p.is_trophy)}
          label="Trophy"
          color="bg-primary"
          testid={`trophy-toggle-${p.id}`}
        />
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function formatRemovedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function RemovedRow({ p, onRestore, onPurge }) {
  const removedBy = p.deleted_by_name || p.deleted_by_email || "unknown";
  const roleLabel = p.deleted_by_role === "captain" ? "captain" : p.deleted_by_role || "user";
  return (
    <div
      className="flex items-center gap-4 p-4 border-b border-border last:border-b-0 opacity-90"
      data-testid={`removed-player-row-${p.id}`}
    >
      <div className="h-14 w-14 bg-secondary border border-border overflow-hidden flex items-center justify-center flex-shrink-0 grayscale">
        {p.photo_path ? (
          <img src={imageUrl(p.photo_path)} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="w-6 h-6 rounded-full othello-disc-dark" />
        )}
      </div>
      <img src={countryFlagUrl(p.country_code)} alt="" className="h-6 w-9 object-cover border border-border grayscale" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate line-through decoration-muted-foreground/60">
          {p.name}
        </div>
        <div className="text-xs text-muted-foreground">
          {p.country_name || p.country_code}
          {p.is_female && " · Female"}
          {p.is_youth && " · Youth"}
        </div>
        <div className="text-xs text-destructive mt-1" data-testid={`removed-meta-${p.id}`}>
          Removed <strong>{formatRemovedAt(p.deleted_at)}</strong> by {roleLabel}{" "}
          <span className="text-muted-foreground">{removedBy}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="uppercase tracking-wide text-[10px]">
          Removed
        </Badge>
        <Button
          size="sm"
          variant="outline"
          onClick={onRestore}
          data-testid={`restore-player-${p.id}`}
        >
          <RotateCcw className="h-4 w-4 mr-1" /> Restore
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onPurge}
          className="text-destructive"
          data-testid={`purge-player-${p.id}`}
          title="Permanently delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminPlayersPage() {
  const { current } = useChampionship() || {};
  const [items, setItems] = useState([]);
  const [removed, setRemoved] = useState([]);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const [{ data: active }, { data: gone }] = await Promise.all([
      api.get(`/admin/championships/${current.id}/players`),
      api.get(`/admin/championships/${current.id}/players/removed`),
    ]);
    setItems(active || []);
    setRemoved(gone || []);
  }, [current?.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p, approved) => {
    try {
      await api.post(`/admin/players/${p.id}/approve`, { approved });
      toast.success(approved ? "Player approved" : "Approval revoked");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleFlag = async (p, field, value) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, [field]: value } : x)));
    try {
      await api.patch(`/admin/players/${p.id}/flags`, { [field]: value });
      const labels = { is_star: "Star", is_trophy: "Champion" };
      toast.success(`${labels[field]} ${value ? "set" : "removed"} for ${p.name}`);
    } catch (err) {
      toast.error(formatApiError(err));
      await load();
    }
  };
  const remove = async (p) => {
    if (!confirm(`Delete player "${p.name}"?`)) return;
    try { await api.delete(`/admin/players/${p.id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const restore = async (p) => {
    try {
      await api.post(`/admin/players/${p.id}/restore`);
      toast.success(`Restored ${p.name}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const purge = async (p) => {
    if (!confirm(`Permanently delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/players/${p.id}`);
      toast.success("Permanently deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pending = items.filter((p) => !p.approved);
  const approved = items.filter((p) => p.approved);

  return (
    <div className="p-8 lg:p-10" data-testid="admin-players">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Players</div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2 mb-2">Player approvals</h1>
      <p className="text-sm text-muted-foreground mb-3">
        Captains submit their country's rosters here. Each player must be approved by an admin before they appear on the public Teams page.
      </p>
      <div className="text-xs text-muted-foreground mb-8 inline-flex flex-wrap items-center gap-x-4 gap-y-1 bg-secondary px-3 py-2">
        <span><strong>Tip:</strong> Click the star/trophy icon to flag a player on the public Teams page.</span>
        <span className="inline-flex items-center gap-1"><span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-yellow-400 text-white text-[10px]">★</span> Star (top-ranked)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white text-[10px]">T</span> Past champion</span>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="mb-6">
          <TabsTrigger value="pending" data-testid="tab-pending-players">
            Pending {pending.length > 0 && <Badge className="ml-2 bg-amber-500 text-white">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-players">
            Approved {approved.length > 0 && <Badge variant="secondary" className="ml-2">{approved.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="removed" data-testid="tab-removed-players">
            Removed {removed.length > 0 && <Badge variant="secondary" className="ml-2">{removed.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <div className="border border-border bg-white">
            {pending.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground" data-testid="no-pending-players">
                No players awaiting approval.
              </div>
            ) : (
              pending.map((p) => (
                <PlayerCard key={p.id} p={p} onToggleFlag={toggleFlag}>
                  <Badge className="bg-amber-500 text-white">Pending</Badge>
                  <Button size="sm" onClick={() => toggle(p, true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`approve-player-${p.id}`}>
                    <Check className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-destructive" data-testid={`delete-player-admin-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </PlayerCard>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="approved">
          <div className="border border-border bg-white">
            {approved.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">No approved players yet.</div>
            ) : (
              approved.map((p) => (
                <PlayerCard key={p.id} p={p} onToggleFlag={toggleFlag}>
                  <Badge className="bg-primary text-primary-foreground">Approved</Badge>
                  <Button size="sm" variant="ghost" onClick={() => toggle(p, false)} data-testid={`revoke-player-${p.id}`}>
                    <X className="h-4 w-4 mr-1" /> Revoke
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </PlayerCard>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="removed">
          <div className="border border-border bg-white">
            {removed.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground" data-testid="no-removed-players">
                No players have been removed.
              </div>
            ) : (
              removed.map((p) => (
                <RemovedRow
                  key={p.id}
                  p={p}
                  onRestore={() => restore(p)}
                  onPurge={() => purge(p)}
                />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
